import {
    Component,
    OnInit,
    Input,
    Output,
    EventEmitter,
    ViewEncapsulation,
    OnDestroy,
} from '@angular/core';
import { FnDateFormatter } from '../../util/date';
import { FnDateConfig } from './fn-date.model';
import { FnLocalStorageService } from '../../services/fn-local-storage.service';
import { hwAny } from '../../util/types';
import { isNotNull } from '../../util/check';
import { DateAdapter } from '@angular/material/core';



/**
 * This Component allows users to choose a single date from the calendar and change Date by next and Prev Button.
 * To use this component import FnDatePickerModule in Module.
 */
@Component({
    selector: 'fn-date-picker',
    templateUrl: 'fn-date-picker.component.html',
    encapsulation: ViewEncapsulation.None,
})
export class FnDatePickerComponent extends FnLocalStorageService implements OnInit, OnDestroy {
    /**
     * Input member of fn date picker component
     * used to set selected Date when selected Date is not given or undefined
     */
    @Input() currentDate = new Date();
    /**
     * Input member of fn date picker component
     * FNDateConfig Object used to set the properties of DatePicker.
     */
    @Input() globalConfig: FnDateConfig;
    /**
     * Input member of fn date picker component
     * used to disable datePicker when true .
     */
    @Input() isDisable = false;

    /**
     * Input member of fn date picker component
     * variable used to show the display format of date.
     */
    @Input() format = 'd/M/Y';
    /**
     * Input member of fn date picker component
     * used to set SelectedDate if not given then Current Date is set.
     */
    @Input() selectedDate: Date;
    /**
     * Input member of fn date picker component
     * variable used to pass Classes to the date picker panel.
     */
    @Input() customClass: string;
    /**
     * Output member of fn date picker component
     * used to emit Event on Date Selection From DatePicker model .
     */
    @Output() callback = new EventEmitter();
    /**
     * Output member of fn date picker component
     * callback used to Disable nextButton when isAutoHandle is False and isFutureDisbale is true.
     */
    @Output() nextDisabledCallback = new EventEmitter();
    /**
     * Output member of fn date picker component.
     * callback used to Disable nextButton.
     */
    @Output() prevDisabledCallback = new EventEmitter();
    /**
     * Output member of fn date picker component.
     * used to emit event when date is change fron next and Prev button.
     */
    @Output() nextPrevCallback = new EventEmitter();

    /**
     * Output member  of fn date picker component.
     * callback used to disabled only some date. When max Date is Null and from isAutoHandler and isFutureDisbale one value is False.
     */
    @Output() dateDisabled = new EventEmitter();
    /**
     * member of fn date picker component.
     * variable used to show the view that the calendar should start in.
     * default value is month view(days of month).
     */
    startView = 'month';

    /**
     * Implements ngOnInit event handler
     * set the value of some variable to set the properties and selected Date.
     */
    formattedDate: any;

    /**
       * varible to show selected date in selected language.
       */
    private langCode = 'en';

    /**
     * varible to subscribe and Unsubscribe datePickerAction
     */
    private isdatePickerAction = false;
    /**
     * Implementing On init Event Handler to set default data
     */


    constructor(private adapter: DateAdapter<any>) {
        super();
    }

    ngOnInit() {
        const langObj = JSON.parse(this.getItem('APP_LANGUAGE')) as hwAny;
        this.langCode = isNotNull(langObj) ? langObj.langCode : 'en';
        this.adapter.setLocale(this.langCode);
        this.isdatePickerAction = this.globalConfig !== undefined;
        if (this.isdatePickerAction) {
            this.globalConfig.datePickerAction.subscribe((config: FnDateConfig) => {
                this.setGlobalConfig(config);
            });
        } else {
            this.globalConfig = new FnDateConfig();
        }
        this.setGlobalConfig(this.globalConfig);
    }
    /**
     * create mthod to set global configuration of date picker
     * set the value of globalConfig variable and change the view of date picker according to weekly,monthly.
     */
    private setGlobalConfig(globalConfig: FnDateConfig) {
        this.globalConfig = this.initGlbConfig(globalConfig);
        this.setDateModel(this.selectedDate || this.currentDate);
        this.customClass =
            this.customClass === undefined
                ? this.globalConfig.isMonthView
                    ? 'fn-date-month-picker'
                    : ''
                : this.customClass;
        this.startView = this.globalConfig.isMonthView ? 'year' : 'month';
    }

    /**
     * Get formatted date method
     * @returns the date as the Format of date is Given.
     */
    getFormattedDate() {
        return new FnDateFormatter().format(
            this.getDateObj(),
            this.globalConfig.format,
            this.langCode
        );
    }

    /**
     * Get date return the selected Date as DateObject.
     * @returns DateObject;
     */
    getDateObj() {
        return new Date(this.selectedDate);
    }

    /**
     * Sets date
     * method invoke when date is Selected. callback event Emitter emit the selectedDate Object
     */
    setDate() {
        this.callback.emit(this.getDateObj());
    }

    /**
     * Next prev handler method calls when either next or prev Button is Clicked.
     * if isAutoHandler is True then increase and decrease date by noOfNavDay value of GlobalConfig Object.
     *  else increase and decrease month if isMonthView is True and else increase and decrease by One Day.
     * @param isNext true when Next button is Click and false if prev button is Clicked .
     */
    nextPrevHandler(isNext: boolean) {
        if (this.globalConfig.isAutoHandler && !this.globalConfig.isMonthView) {
            this.setDateModel(
                isNext
                    ? this.getNextDate(this.globalConfig.noOfNavDay)
                    : this.getPrevDate(this.globalConfig.noOfNavDay)
            );
        } else {
            if (this.globalConfig.isMonthView) {
                this.setDateModel(isNext ? this.getNextMonth() : this.getPrevMonth());
            } else {
                this.setDateModel(isNext ? this.getNextDate(1) : this.getPrevDate(1));
            }
        }
        this.nextPrevCallback.emit({ date: this.getDateObj(), isNext });
    }

    /**
     * Method disabled the nextbutton if return true.
     * if isAutoHandler and isFutureDisable is true and selected Date is not undefined
     * then it return true if nextSelected Value is greater or equal to disableDate else return false.
     * esle iF is isFutureDisable then it get the value boolean from parent Component and return that value.
     * @returns  boolean value.
     */

    nextDisable() {
        if (this.globalConfig.isAutoHandler && this.globalConfig.isFutureDisable) {
            if (this.selectedDate !== undefined) {
                return (
                    new Date(this.getNextDate(this.globalConfig.noOfNavDay)) >
                    this.globalConfig.disabledDate
                );
            }
        } else if (this.globalConfig.isFutureDisable) {
            let isDisable = false;
            this.nextDisabledCallback.emit({
                date: this.getDateObj(),
                callback: (value: boolean) => {
                    isDisable = value;
                },
            });
            return isDisable;
        }
        return false;
    }

    /**
     * disabled the prevButton if it return true.
     * get the value boolean from parent Component and return that value.
     * @returns  boolean value.
     */
    prevDisable() {
        let isDisable = false;
        this.prevDisabledCallback.emit({
            date: this.getDateObj(),
            callback: (value: boolean) => {
                isDisable = value;
            },
        });
        return isDisable;
    }

    /**
     * Sets selectDate value
     * @param date Date Object.
     */
    setDateModel(date: Date) {
        this.selectedDate = date;
    }

    /**
     * Date picker filter of fn date picker component
     * Function that can be used to filter out dates within the datepicker.
     */
    datePickerFilter = (date: Date) => {
        if (date === null) {
            return false;
        }
        if (this.globalConfig.maxDate !== null) {
            return false;
        }
        if (this.globalConfig.isAutoHandler && this.globalConfig.isFutureDisable) {
            return new Date(date.toString()) <= this.globalConfig.disabledDate;
        }
        let returnVal = true;
        this.dateDisabled.emit({
            date: new Date(date.toString()),
            callback: (val: boolean) => {
                returnVal = val;
            },
        });
        return returnVal;
    };

    /**
     * Gets next date method return the new DateObject by increasing the no of days pass as @param.
     * @param noOfNavDay of type number added in date value.
     * @returns  new Date Object.
     */
    getNextDate(noOfNavDay: number) {
        const date = this.getDateObj();
        return new Date(date.setDate(date.getDate() + noOfNavDay));
    }

    /**
     * Gets prev date return the new DateObject by decreasing the no of days pass as @param.
     * @param  noOfNavDay of type number subtracted in date value.
     * @returns  new Date Object.
     */
    getPrevDate(noOfNavDay: number) {
        const date = this.getDateObj();
        return new Date(date.setDate(date.getDate() - noOfNavDay));
    }

    /**
     * Method return the next Month to selected Date.
     * @returns  new Date Object.
     */
    getNextMonth() {
        const date = this.getDateObj();
        return new Date(date.getFullYear(), date.getMonth() + 1, 1);
    }

    /**
     *  Method return the Previous Month to selected Date.
     * @returns date object
     */
    getPrevMonth() {
        const date = this.getDateObj();
        return new Date(date.getFullYear(), date.getMonth() - 1, 1);
    }

    /**
     * Months selected handler this method called when monthView is True.
     * @param event date value
     * @param datePicker DatePikcer Object for closing DatePicker Modal.
     */
    _monthSelectedHandler(event: any, datePicker: any) {
        if (this.globalConfig.isMonthView) {
            this.setDateModel(new Date(event));
            this.setDate();
            datePicker.close();
        }
    }

    /**
     * Method set global config Object value and if some member
     * value is not passed then it set them as default FnDateConfig object member value.
     * @param configHash FnDateConfig object passed as input
     * @returns FnDateConfig object.
     */
    initGlbConfig(configHash: FnDateConfig) {
        const returnGlbHash: any = Object.assign(configHash, {});
        const defaultCnfHash: any = new FnDateConfig();
        Object.keys(defaultCnfHash).forEach((key) => {
            if (!returnGlbHash.hasOwnProperty(key)) {
                returnGlbHash[key] = defaultCnfHash[key];
            } else if (key === 'type') {
                if (returnGlbHash[key] === 'month') {
                    returnGlbHash.isMonthView = true;
                } else {
                    returnGlbHash.isMonthView = false;
                }
            }
        });
        return returnGlbHash;
    }
    /**
     * created destroy method to unsubsribe the method.
     */
    ngOnDestroy() {
        if (this.isdatePickerAction) {
            this.globalConfig.datePickerAction.unsubscribe();
        }
    }
}

